import React from 'react'
import { ProductCard, productStore } from '../../product'
import { Accordion, Button, Loader } from '../../shared'
import { ICollectionLink } from '../collections.interfaces'
import { CollectionView } from '../collections.enums'
import { reorderCollectionProductLimit, customerOrders } from '../collections.data';
import { IReorderItem } from '../../../interfaces'
import { cartStore } from '../../cart'
import { collectionsStore } from '../collections.store'
import { formatMoney, getFeatureFlag } from '../../../utils'
import './reorder-collection.scss'
import { TagAction, useFavouriteOrder } from '../../../hooks'
import { observer } from 'mobx-react'
import { CollectionBanner } from '../collection-banner'
import { modalStore } from '../../shared'
import { FavouriteOrderBtn } from '../favourite-order-btn'
import { FavouriteTagErrorModal } from './favourite-tag-error-modal'
import { filterBarStore } from '../filter-bar'
import { AlertIcon } from './alert-icon.component'

const isCollectionBannerEnabled = getFeatureFlag('isCollectionBannerEnabled');
interface IProps {
  collectionRef: React.RefObject<HTMLDivElement>
  index: number
  link: ICollectionLink
}

interface IFavouriteOrderProps extends IProps {
  setFavouriteOrder?: Function
  updateOrderTags?: Function
}

interface IState {
  loading: boolean
  tagsLoading: boolean
}

export function withFavouriteOrderHook<T>(Component: React.ComponentType<T>): React.ComponentType<T> {
  return function WrappedComponent(props: T) {
    const { setFavouriteOrder, updateOrderTags } = useFavouriteOrder();
    return <Component {...props as T} setFavouriteOrder={setFavouriteOrder} updateOrderTags={updateOrderTags} />;
  }
}

@observer
class ReorderCollection extends React.Component<IFavouriteOrderProps, IState> {
  state: IState = {
    loading: false,
    tagsLoading: false
  }

  accordion: React.RefObject<Accordion> = React.createRef()

  componentDidMount(): void {
    this.setState({ loading: true })

    // This will set the quantities of items in the reorder
    collectionsStore.validReorderItems.filter(item => item.product.available).forEach((item: IReorderItem) => {
      if (!item.product.available) {
        return
      }

      cartStore.itemQuantityInputs.set(item.product.id, item.quantity.toString())
    })

    this.setState({ loading: false })
  }

  getModalHeading(itemsToDisplay: IReorderItem[], unavailableOrderItems: IReorderItem[], outOfStockOrderItems: IReorderItem[]): JSX.Element {

    if (collectionsStore.reOrderableOrderItems.length !== itemsToDisplay.length) {
      if (unavailableOrderItems.length === itemsToDisplay.length) { // all the products are unavailable
        return (<>All products are<br/>no longer on the menu</>)
      } else if (outOfStockOrderItems.length === itemsToDisplay.length) { // all the products are out of stock
        return (<>All products are<br/>out of stock</>)
      } else if (unavailableOrderItems.length + outOfStockOrderItems.length === itemsToDisplay.length) { // all the products either out of stock or unavailable
        return (<>All products are<br/>not available</>)
      } else if (unavailableOrderItems.length > 0 && outOfStockOrderItems.length > 0 && unavailableOrderItems.length + outOfStockOrderItems.length < itemsToDisplay.length) { // some products are out of stock; and some are unavailable
        return (<>Some products are<br/>not available</>)
      } else if (unavailableOrderItems.length > 0) { // some products are unavailable
        return (<>Some products are<br/>no longer on the menu</>)
      } else { // some products are out of stock
        return (<>Some products are<br/>out of stock</>)
      }
    } else {
      return null;
    }
  }

  getModalButton(itemsToDisplay: IReorderItem[], unavailableOrderItems: IReorderItem[], outOfStockOrderItems: IReorderItem[]): JSX.Element {
    if (collectionsStore.reOrderableOrderItems.length !== itemsToDisplay.length) {
      if (unavailableOrderItems.length === itemsToDisplay.length ||
          outOfStockOrderItems.length === itemsToDisplay.length ||
          unavailableOrderItems.length + outOfStockOrderItems.length === itemsToDisplay.length) {
        return <>OK</>
      } else {
        return <>Continue</>
      }
    } else {
      return null;
    }
  }

  openModal = (): void => {
    // Warning if there is any out of stock or unavailable items
    const itemsToDisplay = filterBarStore.selectedFilters.length > 0 || filterBarStore.searchTerm
      ? filterBarStore.getFilteredSearchedReorderProducts(collectionsStore.validReorderItems)
      : [...collectionsStore.validReorderItems]
    const unavailableOrderItems = filterBarStore.searchTerm ? collectionsStore.getUnavailableOrderItems(itemsToDisplay) : collectionsStore.unavailableOrderItems
    const outOfStockOrderItems = filterBarStore.searchTerm ? collectionsStore.getOutOfStockOrderItems(itemsToDisplay) : collectionsStore.outOfStockOrderItems

    if (collectionsStore.reOrderableOrderItems.length !== itemsToDisplay.length) {
      const outOfStockModal: JSX.Element = (
        <div style={{ textAlign: 'center', padding: '30px', backgroundColor: `${outOfStockOrderItems.length > 0 && unavailableOrderItems.length === 0 ? 'rgba(255, 219, 167, 0.5)' : 'rgba(203, 5, 0, 0.1)'}` }}>
          <AlertIcon />
          <h3 style={{ fontSize: '24px', lineHeight: '38px', textTransform: 'uppercase' }}>{this.getModalHeading(itemsToDisplay, unavailableOrderItems, outOfStockOrderItems)}</h3>
          <p style={{ fontSize: '18px', lineHeight: '24px' }}>
            But, don't worry!<br/>
            We have other products that you'll love.
          </p>
          <Button onClick={() => {
            if (collectionsStore.reOrderableOrderItems.length !== 0) {
              this.addOrderToCart()
            }
            modalStore.toggle()
          }}>{this.getModalButton(itemsToDisplay, unavailableOrderItems, outOfStockOrderItems)}</Button>
        </div>
      )

      modalStore.set(outOfStockModal)
      modalStore.toggle()
    } else {
      this.addOrderToCart()
    }
  }

  showErrorMessageModal = (): void => {
    modalStore.set(<FavouriteTagErrorModal callback={modalStore.toggle} />)
    modalStore.toggle()
  }

  addOrderToCart = (): void => {
    this.setState({loading: true})

    collectionsStore.reOrderableOrderItems.forEach((item) => {
      // Only set reorder quantity if it's less than current cart quantity
      if (cartStore.items.get(item.product.id) && cartStore.items.get(item.product.id).quantity > item.quantity) {
        return
      }

      cartStore.itemQuantityInputs.set(item.product.id, item.quantity.toString())
      cartStore.updateOrAddItemToCart(item.product, item.quantity)
    })

    if (this.accordion.current && !this.accordion.current.state.active) {
      this.accordion.current.toggleAccordion()
    }

    this.setState({loading: false})
  }

  handleUpdateSuccess = () => {
    setTimeout(() => {
      window.location.reload()
    }, 1000)
  }

  handleSetFavourite = async () => {
    this.setState({ tagsLoading: true })

    try {
      if (collectionsStore.reOrderableOrder.isFavourite) { // remove the existing favourite order tag
        const orderId = await this.props.updateOrderTags(collectionsStore.reOrderableOrder, TagAction.REMOVE)
        if (orderId) {
          return this.handleUpdateSuccess()
        } else {
          throw new Error('Removing the tag of the favourite order is failed.')
        }
      } else { // set the new favourite order tag
        const orderId = await this.props.setFavouriteOrder(collectionsStore.reOrderableOrder)
        if (orderId) {
          this.handleUpdateSuccess()
        } else {
          throw new Error('Setting the tag of the favourite order is failed.')
        }
      }
    } catch {
      this.setState({ tagsLoading: false })
      this.showErrorMessageModal()
    }
  }

  get isListView(): boolean {
    return productStore.collectionView === CollectionView.LIST
  }

  render(): JSX.Element {
    const baseClass: string = 'reorder-collection'

    // get all valid items, including out of stock items
    const itemsToDisplay =
    filterBarStore.selectedFilters.length > 0 || filterBarStore.searchTerm
      ? filterBarStore.getFilteredSearchedReorderProducts(collectionsStore.validReorderItems)
      : [...collectionsStore.validReorderItems]

    const { collectionRef, index, link, loading, tagsLoading } = { ...this.props, ...this.state }
    const qtyReorderableOrderItems = filterBarStore.searchTerm ? collectionsStore.getReOrderableOrderItems(itemsToDisplay).length : collectionsStore.reOrderableOrderItems.length
    const qtyOutOfStockItems = filterBarStore.searchTerm ? collectionsStore.getOutOfStockOrderItems(itemsToDisplay).length : collectionsStore.outOfStockOrderItems.length
    const qtyUnavailableItems = filterBarStore.searchTerm ? collectionsStore.getUnavailableOrderItems(itemsToDisplay).length : collectionsStore.unavailableOrderItems.length

    const header = (
      <>
        {/* Feature toggle when enabled will display a banner for this collection */}
        { isCollectionBannerEnabled && (
            <CollectionBanner title={link.title} />
          )
        }

        <div className={`${baseClass}__header`}>
          {/* Add to favourite */}
          <FavouriteOrderBtn
            className={`${baseClass}__fav-order-btn`}
            handleSetFavourite={this.handleSetFavourite}
            isFavourite={collectionsStore.reOrderableOrder.isFavourite}
            loading={tagsLoading}
          />
          <p className="in-stock">{qtyReorderableOrderItems} product{qtyReorderableOrderItems > 1 && 's'} in stock</p>

          {qtyOutOfStockItems > 0 && (
            <p className="out-of-stock">{qtyOutOfStockItems || 'No'} product{qtyOutOfStockItems > 1 && 's'} out of stock</p>
          )}

          {qtyUnavailableItems > 0 && (
            <p className="removed-items">{qtyUnavailableItems} product{qtyUnavailableItems > 1 && 's'} unavailable</p>
          )}

          <Button
            gtmId={collectionsStore.reOrderableOrder.isFavourite ? 'favourite-order-add-to-cart' : 'last-order-add-to-cart'}
            class={`${baseClass}__button`}
            onClick={this.openModal}
            size="small"
            display="narrow"
            disabled={loading || !collectionsStore.canAddReorderToCart}
          >
            <span>
              {loading
                ? <Loader size="small" color="grey" />
                : formatMoney(Math.round(100 * collectionsStore.reorderCost))
              }
            </span>
            <div title={collectionsStore.reOrderableOrder.name}>Add order to cart</div>
          </Button>
        </div>
      </>
    )

    if (collectionsStore.reOrderableOrder && itemsToDisplay.length === 0) {
      return (
        <>
          {header}
          <div ref={collectionRef} className={`${baseClass} collection-list__empty`} key={index}>
            <p>No exact matches.<br/>Try changing or removing some of your filters.</p>
          </div>
        </>
      )
    }

    return (
      collectionsStore.reOrderableOrder && itemsToDisplay.length > 0 ? <div ref={collectionRef} className={`${baseClass} collection-list__item`} key={index}>
        {header}
        <div
          className={`collection-list__link ${this.isListView ? 'list row row--flush' : 'grid row row--small'}`}
          id={link.handle}
        >
          {itemsToDisplay.length > 0 && itemsToDisplay.splice(0, reorderCollectionProductLimit).map(item =>
            <ProductCard
              {...item.product}
              displayUnavailable={true}
              narrowRow
              key={`reorder__${item.product.handle}__${item.quantity}`} />)
          }

          {itemsToDisplay.length > 0 && (
            <Accordion
              ref={this.accordion}
              className={`${baseClass}__accordion`}
              labelShow="View more"
              labelHide="View less"
              content="above">
              {itemsToDisplay.map(item =>
                <ProductCard
                  {...item.product}
                  displayUnavailable={true}
                  narrowRow
                  key={`reorder__${item.product.handle}__${item.quantity}`} />)
              }
            </Accordion>
          )}
        </div>
      </div>
      :
      null
    )
  }
}

export default withFavouriteOrderHook(ReorderCollection)